import { shallowRef, computed, toValue, watch } from 'vue'
import type { MaybeRefOrGetter } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { FilterSelectField } from '@/types/filter'

const FILTER_PREFIX = 'filter:'

export function clearFilterStorage() {
  const keys = Object.keys(localStorage).filter((k) => k.startsWith(FILTER_PREFIX))
  keys.forEach((k) => localStorage.removeItem(k))
}

type FilterStorageOptions =
  | { storage?: 'url'; storageKey?: MaybeRefOrGetter<string> }
  | { storage: 'local'; storageKey: MaybeRefOrGetter<string> }

export function useFilter(
  filterFields: MaybeRefOrGetter<FilterSelectField[]>,
  storageOptions: FilterStorageOptions = {},
) {
  const route = useRoute()
  const router = useRouter()

  const isLocal = storageOptions.storage === 'local'
  const storageKey = storageOptions.storageKey ?? 'filter'
  const getKey = () => `${FILTER_PREFIX}${toValue(storageKey)}`
  const paramKey = (key: string) => `${toValue(storageKey)}[${key}]`

  const isSheetOpen = shallowRef(false)

  function buildDefaults(): Record<string, string | string[]> {
    const result: Record<string, string | string[]> = {}
    for (const field of toValue(filterFields)) {
      if (field.default !== undefined) {
        result[field.key] = field.default
      }
    }
    return result
  }

  function readFromStorage(): Record<string, string | string[]> {
    if (isLocal) {
      try {
        return JSON.parse(localStorage.getItem(getKey()) ?? '{}')
      } catch {
        return {}
      }
    }
    const result: Record<string, string | string[]> = {}
    for (const field of toValue(filterFields)) {
      const value = route.query[paramKey(field.key)]
      if (value !== undefined && value !== null) {
        result[field.key] = value as string | string[]
      }
    }
    return result
  }

  function writeToStorage(nonDefaultValues: Record<string, string | string[]>) {
    if (isLocal) {
      if (Object.keys(nonDefaultValues).length === 0) {
        localStorage.removeItem(getKey())
      } else {
        localStorage.setItem(getKey(), JSON.stringify(nonDefaultValues))
      }
      return
    }
    const filterParams = Object.fromEntries(
      Object.entries(nonDefaultValues).map(([k, v]) => [paramKey(k), v]),
    )
    const cleanedQuery = Object.fromEntries(
      Object.entries(route.query).filter(([k]) => !k.startsWith(`${getKey()}[`)),
    )
    router.replace({ query: { ...cleanedQuery, ...filterParams } })
  }

  const defaults = computed(() => buildDefaults())
  const appliedSelection = shallowRef<Record<string, string | string[]>>({
    ...defaults.value,
    ...readFromStorage(),
  })
  const pendingSelection = shallowRef<Record<string, string | string[]>>({
    ...appliedSelection.value,
  })

  const hasActiveFilters = computed(() => {
    const defs = defaults.value
    const applied = appliedSelection.value
    const allKeys = new Set([...Object.keys(defs), ...Object.keys(applied)])
    for (const key of allKeys) {
      if (applied[key] !== defs[key]) {
        return true
      }
    }
    return false
  })

  watch(
    () => toValue(filterFields),
    () => {
      const next = { ...defaults.value, ...readFromStorage() }
      appliedSelection.value = next
      pendingSelection.value = { ...next }
    },
  )

  watch(
    () => getKey(),
    () => {
      const next = { ...defaults.value, ...readFromStorage() }
      appliedSelection.value = next
      pendingSelection.value = { ...next }
    },
  )

  function openSheet() {
    pendingSelection.value = { ...appliedSelection.value }
    isSheetOpen.value = true
  }

  function applyFilters() {
    appliedSelection.value = { ...pendingSelection.value }
    isSheetOpen.value = false

    const defs = defaults.value
    const nonDefaultEntries = Object.entries(appliedSelection.value).filter(
      ([k, v]) => v !== defs[k],
    )
    writeToStorage(Object.fromEntries(nonDefaultEntries))
  }

  function resetFilters() {
    const defs = defaults.value
    pendingSelection.value = { ...defs }
    appliedSelection.value = { ...defs }
    isSheetOpen.value = false

    writeToStorage({})
  }

  function selectOption(key: string, value: string | string[]) {
    if (pendingSelection.value[key] === value) {
      return
    }

    pendingSelection.value = { ...pendingSelection.value, [key]: value }
  }

  function removeFilter(key: string) {
    const defs = defaults.value
    const next = { ...appliedSelection.value }
    if (defs[key] !== undefined) {
      next[key] = defs[key]
    } else {
      delete next[key]
    }
    appliedSelection.value = next
    pendingSelection.value = { ...next }

    const nonDefaultEntries = Object.entries(next).filter(([k]) => next[k] !== defs[k])
    writeToStorage(Object.fromEntries(nonDefaultEntries))
  }

  return {
    isSheetOpen,
    openSheet,
    pendingSelection,
    appliedSelection,
    defaults,
    hasActiveFilters,
    selectOption,
    applyFilters,
    resetFilters,
    removeFilter,
  }
}
